import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { fmtDate } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";

const L_STYLE = {
  Pending: "bg-warning text-warning-foreground border-warning",
  Approved: "bg-success-tint text-success border-success/30",
  Rejected: "bg-danger text-danger-foreground border-danger",
};

export default function Leaves() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const canDecide = ["super_admin", "admin"].includes(user.role);
  const load = useCallback(() => api.get("/leaves").then((r) => setLeaves(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (l, decision) => {
    try {
      await api.post(`/leaves/${l.id}/${decision}`);
      toast.success(`Leave ${decision}d — employee notified`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="leaves-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Leave Requests</h1>
        <p className="text-sm text-muted-foreground">All requests permanently logged — no more verbal approvals</p>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="leaves-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Employee</th><th className="px-4 py-3">From</th><th className="px-4 py-3">To</th>
              <th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Decided By</th>
              {canDecide && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {leaves.map((l) => (
              <tr key={l.id} data-testid={`leave-row-${l.id}`} className="border-t hover:bg-accent/40 transition-colors">
                <td className="px-4 py-3 font-medium">{l.employee_name}</td>
                <td className="px-4 py-3">{fmtDate(l.from_date)}</td>
                <td className="px-4 py-3">{fmtDate(l.to_date)}</td>
                <td className="px-4 py-3 text-muted-foreground max-w-[240px] truncate">{l.reason || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${L_STYLE[l.status] || ""}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{l.approved_by || "—"}</td>
                {canDecide && (
                  <td className="px-4 py-3">
                    {l.status === "Pending" && (
                      <div className="flex gap-1.5 justify-end">
                        <Button size="sm" data-testid={`leave-approve-${l.id}`} onClick={() => decide(l, "approve")} className="h-8 bg-success text-inverse hover:bg-success/90"><Check size={14} /></Button>
                        <Button size="sm" variant="destructive" data-testid={`leave-reject-${l.id}`} onClick={() => decide(l, "reject")} className="h-8"><X size={14} /></Button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {leaves.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No leave requests</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
